/** @file OverpaintDialog: a painted guide cut by the guide's regions into slot images, then one fill batch. */
import type { ReactElement } from 'react';
import { useMemo, useState } from 'react';
import { ArtModal } from '../ArtModal';
import { overpaintCut } from './overpaintCut';
import { SHEET_FACINGS } from './sheetFacings';
import type { CutPart, GuideSheet } from './sheetTypes';
import { useGuide } from './useGuide';
import { useImageUrls } from './useImageUrls';
import type { OverpaintState } from './useSheetImport';

/** The painting, the guide loader, whether a fill runs, and the fill / close callbacks. */
export type OverpaintDialogProps = {
  overpaint: OverpaintState; loadGuide: (deg: number) => Promise<GuideSheet>; busy: boolean;
  onFill: (cuts: CutPart[]) => void; onClose: () => void;
};

const BUTTON = { padding: '3px 10px', fontSize: 12 };
const SELECT = { fontSize: 12, background: '#15171b', color: '#e6e8ec', border: '1px solid #44474f', borderRadius: 4 };

/** The facing (from the file name), the painting, one row per cut region (slot image 4x, slot key, pixels), Fill. */
export function OverpaintDialog({ overpaint, loadGuide, busy, onFill, onClose }: OverpaintDialogProps): ReactElement {
  const [facing, setFacing] = useState(overpaint.facingDeg);
  const { guide, error } = useGuide(loadGuide, facing);
  const cuts = useMemo(() => (guide === null ? [] : overpaintCut(guide, overpaint.painted)), [guide, overpaint.painted]);
  const urls = useImageUrls(useMemo(() => [overpaint.painted, ...cuts.map((c) => c.image)], [overpaint.painted, cuts]));
  const resized = guide !== null && (guide.image.width !== overpaint.painted.width || guide.image.height !== overpaint.painted.height);
  return (
    <ArtModal label="Overpaint sheet" wide>
      <strong>Overpaint sheet <code>{overpaint.name}</code></strong>
      <label style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        Facing of the guide
        <select aria-label="Overpaint facing" value={facing} style={SELECT} onChange={(e) => { setFacing(Number(e.target.value)); }}>
          {SHEET_FACINGS.map((f) => <option key={f.deg} value={f.deg}>{f.label} {f.deg}</option>)}
        </select>
      </label>
      {error !== null ? <span role="alert">Guide: {error}</span> : null}
      {resized ? <span>The painting is {overpaint.painted.width}x{overpaint.painted.height}; it is scaled to the guide's {guide.image.width}x{guide.image.height}.</span> : null}
      <div style={{ display: 'flex', gap: 12, minHeight: 0 }}>
        {urls[0] === undefined || urls[0] === '' ? null : <img src={urls[0]} alt="painting" style={{ height: 'min(60vh, 560px)', imageRendering: 'pixelated', background: '#111' }} />}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, overflow: 'auto', minWidth: 220 }}>
          {cuts.map((c, k) => (
            <div key={c.slot} data-testid="cut-row" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <span style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#15171b' }}>
                {urls[k + 1] === '' ? null : <img src={urls[k + 1]} alt={c.slot} style={{ width: c.image.width * 2, imageRendering: 'pixelated' }} />}
              </span>
              <code>{c.slot}</code>
              <span style={{ color: '#a9adb6' }}>{c.pixels} px</span>
            </div>
          ))}
          {guide !== null && cuts.length === 0 ? <span>No region holds paint: is this the guide of this facing?</span> : null}
        </div>
      </div>
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <button type="button" style={BUTTON} onClick={onClose}>Cancel</button>
        <button type="button" style={BUTTON} disabled={busy || cuts.length === 0} onClick={() => { onFill(cuts); }}>Fill {cuts.length} slots</button>
      </div>
    </ArtModal>
  );
}
